import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BuildingOfficeIcon,
  PhotoIcon,
  MapPinIcon,
  Cog6ToothIcon,
  ClockIcon,
  ShieldCheckIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import GeofenceMapPicker from '../components/GeofenceMapPicker';
import apiService from '../services/api';
import { useCompany } from '../contexts/CompanyContext';
import toast from 'react-hot-toast';

const CompanySettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const { fetchCompanyInfo } = useCompany();

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      formatted: ''
    },
    geofence: {
      latitude: 51.505,
      longitude: -0.09,
      radius: 100,
      description: 'Main office location'
    },
    settings: {
      timeZone: 'UTC',
      workingHours: {
        start: '09:00',
        end: '17:00'
      },
      workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      allowRemoteWork: false,
      strictGeofencing: true
    }
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const workingDayOptions = [
    { value: 'monday', label: t('companySettings.days.monday') },
    { value: 'tuesday', label: t('companySettings.days.tuesday') },
    { value: 'wednesday', label: t('companySettings.days.wednesday') },
    { value: 'thursday', label: t('companySettings.days.thursday') },
    { value: 'friday', label: t('companySettings.days.friday') },
    { value: 'saturday', label: t('companySettings.days.saturday') },
    { value: 'sunday', label: t('companySettings.days.sunday') }
  ];

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCompanySettings();
      
      if (response.success && response.company) {
        const companyData = response.company;
        setCompany(companyData);
        
        setFormData({
          name: companyData.name || '',
          address: {
            street: companyData.address?.street || '',
            city: companyData.address?.city || '',
            state: companyData.address?.state || '',
            postalCode: companyData.address?.postalCode || '',
            country: companyData.address?.country || '',
            formatted: companyData.address?.formatted || ''
          },
          geofence: {
            latitude: companyData.geofence?.latitude || 51.505,
            longitude: companyData.geofence?.longitude || -0.09,
            radius: companyData.geofence?.radius || 100,
            description: companyData.geofence?.description || 'Main office location'
          },
          settings: {
            timeZone: companyData.settings?.timeZone || 'UTC',
            workingHours: {
              start: companyData.settings?.workingHours?.start || '09:00',
              end: companyData.settings?.workingHours?.end || '17:00'
            },
            workingDays: companyData.settings?.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            allowRemoteWork: companyData.settings?.allowRemoteWork || false,
            strictGeofencing: companyData.settings?.strictGeofencing !== false
          }
        });

        if (companyData.logo?.url) {
          setLogoPreview(companyData.logo.url);
        }
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
      // Don't show error for 404 - just means no company configured yet
      if (error.message !== 'Company settings not found') {
        toast.error(t('companySettings.messages.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const actualValue = type === 'checkbox' ? checked : value;

    if (name.includes('.')) {
      const [section, field, subfield] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subfield ? field : field]: subfield ? {
            ...prev[section][field],
            [subfield]: actualValue
          } : actualValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: actualValue
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleWorkingDaysChange = (day) => {
    const currentDays = formData.settings.workingDays;
    const updatedDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];

    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        workingDays: updatedDays
      }
    }));
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
        toast.error(t('companySettings.messages.invalidImageType', { defaultValue: 'Please select a JPEG or PNG image' }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('companySettings.messages.imageTooLarge', { defaultValue: 'Image must be smaller than 5MB' }));
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = async () => {
    if (company?.logo?.url) {
      try {
        await apiService.deleteCompanyLogo();
        // Refresh company info in global context
        await fetchCompanyInfo();
        toast.success(t('companySettings.messages.logoRemoved'));
      } catch (error) {
        toast.error(t('companySettings.messages.failedToRemoveLogo'));
        return;
      }
    }

    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLocationChange = (location, address) => {
    setFormData(prev => ({
      ...prev,
      geofence: {
        ...prev.geofence,
        latitude: location.lat,
        longitude: location.lng
      }
    }));

    if (address) {
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          ...address,
          formatted: Object.values(address).filter(Boolean).join(', ')
        }
      }));
    }
  };

  const handleRadiusChange = (radius) => {
    setFormData(prev => ({
      ...prev,
      geofence: {
        ...prev.geofence,
        radius
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('companySettings.validation.companyNameRequired');
    }

    if (!formData.geofence.latitude || !formData.geofence.longitude) {
      newErrors.location = t('companySettings.validation.locationRequired');
    }

    if (formData.geofence.radius < 10 || formData.geofence.radius > 10000) {
      newErrors.radius = t('companySettings.validation.radiusInvalid');
    }

    if (formData.settings.workingDays.length === 0) {
      newErrors.workingDays = t('companySettings.validation.workingDaysRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('companySettings.messages.fixValidationErrors', { defaultValue: 'Please fix the validation errors' }));
      return;
    }

    setSaving(true);
    try {
      const submitData = new FormData();

      // Add form data
      submitData.append('name', formData.name);
      submitData.append('address.street', formData.address.street);
      submitData.append('address.city', formData.address.city);
      submitData.append('address.state', formData.address.state);
      submitData.append('address.postalCode', formData.address.postalCode);
      submitData.append('address.country', formData.address.country);
      submitData.append('address.formatted', formData.address.formatted);
      
      submitData.append('geofence.latitude', formData.geofence.latitude.toString());
      submitData.append('geofence.longitude', formData.geofence.longitude.toString());
      submitData.append('geofence.radius', formData.geofence.radius.toString());
      submitData.append('geofence.description', formData.geofence.description);

      submitData.append('settings.timeZone', formData.settings.timeZone);
      submitData.append('settings.workingHours.start', formData.settings.workingHours.start);
      submitData.append('settings.workingHours.end', formData.settings.workingHours.end);
      submitData.append('settings.allowRemoteWork', formData.settings.allowRemoteWork.toString());
      submitData.append('settings.strictGeofencing', formData.settings.strictGeofencing.toString());

      // Add working days
      formData.settings.workingDays.forEach(day => {
        submitData.append('settings.workingDays[]', day);
      });

      // Add logo if selected
      if (logoFile) {
        submitData.append('logo', logoFile);
      }

      const response = await apiService.updateCompanySettings(submitData);

      if (response.success) {
        toast.success(t('companySettings.messages.settingsSaved'));
        await fetchCompanySettings();
        // Refresh company info in global context
        await fetchCompanyInfo();
        setLogoFile(null);
      }
    } catch (error) {
      console.error('Error saving company settings:', error);
      toast.error(error.message || t('companySettings.messages.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-dark-300">{t('companySettings.loadingCompanySettings')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('companySettings.title')}</h1>
          <p className="text-dark-300">{t('companySettings.subtitle')}</p>
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="card">
          <div className="flex items-center mb-4">
            <BuildingOfficeIcon className="h-5 w-5 text-primary-500 mr-2" />
            <h2 className="text-lg font-semibold text-white">{t('companySettings.companyInformation')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-dark-200 mb-2">
                {t('companySettings.companyName')} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 bg-dark-700 border rounded-lg text-white placeholder-dark-400 focus:ring-2 focus:ring-primary-500 ${
                  errors.name ? 'border-red-500' : 'border-dark-600'
                }`}
                placeholder={t('companySettings.companyName')}
              />
              {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Company Logo */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-dark-200 mb-2">
                {t('companySettings.companyLogo')}
              </label>
              
              <div className="flex items-start space-x-4">
                {/* Logo Preview */}
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt={t('companySettings.companyLogo')}
                        className="w-20 h-20 object-cover rounded-lg border border-dark-600"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 bg-dark-700 border border-dark-600 rounded-lg flex items-center justify-center">
                      <PhotoIcon className="h-8 w-8 text-dark-400" />
                    </div>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleLogoSelect}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="btn btn-secondary mb-2"
                  >
                    {t('companySettings.chooseLogo')}
                  </button>
                  <p className="text-xs text-dark-400">
                    {t('companySettings.logoRequirements')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location & Geofencing */}
        <div className="card">
          <div className="flex items-center mb-4">
            <MapPinIcon className="h-5 w-5 text-primary-500 mr-2" />
            <h2 className="text-lg font-semibold text-white">{t('companySettings.locationGeofencing')}</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-dark-800 border border-dark-600 rounded-lg p-4">
              <div className="flex items-start mb-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 mt-0.5 mr-2" />
                <div className="text-sm text-dark-200">
                  <div className="font-medium">{t('companySettings.locationAccessControl')}</div>
                  <div className="mt-1 text-dark-400">
                    {t('companySettings.locationAccessDescription')}
                  </div>
                </div>
              </div>
            </div>

            <GeofenceMapPicker
              initialLocation={{ 
                lat: formData.geofence.latitude, 
                lng: formData.geofence.longitude 
              }}
              initialRadius={formData.geofence.radius}
              onLocationChange={handleLocationChange}
              onRadiusChange={handleRadiusChange}
            />

            {errors.location && (
              <p className="text-red-400 text-sm">{errors.location}</p>
            )}
            {errors.radius && (
              <p className="text-red-400 text-sm">{errors.radius}</p>
            )}
          </div>
        </div>

        {/* Working Hours & Settings */}
        <div className="card">
          <div className="flex items-center mb-4">
            <Cog6ToothIcon className="h-5 w-5 text-primary-500 mr-2" />
            <h2 className="text-lg font-semibold text-white">{t('companySettings.workingHoursPolicies')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Working Hours */}
            <div>
              <label className="block text-sm font-medium text-dark-200 mb-2">
                <ClockIcon className="h-4 w-4 inline mr-1" />
                {t('companySettings.workingHours')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-dark-400 mb-1">{t('companySettings.startTime')}</label>
                  <input
                    type="time"
                    name="settings.workingHours.start"
                    value={formData.settings.workingHours.start}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-dark-400 mb-1">{t('companySettings.endTime')}</label>
                  <input
                    type="time"
                    name="settings.workingHours.end"
                    value={formData.settings.workingHours.end}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Time Zone */}
            <div>
              <label className="block text-sm font-medium text-dark-200 mb-2">
                {t('companySettings.timeZone')}
              </label>
              <select
                name="settings.timeZone"
                value={formData.settings.timeZone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
              >
                <option value="UTC">{t('companySettings.timeZones.utc')}</option>
                <option value="America/New_York">{t('companySettings.timeZones.easternTime')}</option>
                <option value="America/Chicago">{t('companySettings.timeZones.centralTime')}</option>
                <option value="America/Denver">{t('companySettings.timeZones.mountainTime')}</option>
                <option value="America/Los_Angeles">{t('companySettings.timeZones.pacificTime')}</option>
                <option value="Europe/London">{t('companySettings.timeZones.gmt')}</option>
                <option value="Europe/Berlin">{t('companySettings.timeZones.centralEuropeanTime')}</option>
              </select>
            </div>

            {/* Working Days */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-dark-200 mb-2">
                {t('companySettings.workingDays')}
              </label>
              <div className="flex flex-wrap gap-2">
                {workingDayOptions.map(day => (
                  <label key={day.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.settings.workingDays.includes(day.value)}
                      onChange={() => handleWorkingDaysChange(day.value)}
                      className="h-4 w-4 text-primary-600 rounded border-dark-600 bg-dark-700 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-dark-200">{day.label}</span>
                  </label>
                ))}
              </div>
              {errors.workingDays && (
                <p className="text-red-400 text-sm mt-1">{errors.workingDays}</p>
              )}
            </div>

            {/* Policies */}
            <div className="md:col-span-2 space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="settings.allowRemoteWork"
                  checked={formData.settings.allowRemoteWork}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 rounded border-dark-600 bg-dark-700 focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-dark-200">
                  {t('companySettings.allowRemoteWork')}
                  <span className="block text-xs text-dark-400">
                    {t('companySettings.allowRemoteWorkDescription')}
                  </span>
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="settings.strictGeofencing"
                  checked={formData.settings.strictGeofencing}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 rounded border-dark-600 bg-dark-700 focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-dark-200">
                  <ShieldCheckIcon className="h-4 w-4 inline mr-1" />
                  {t('companySettings.strictGeofencing')}
                  <span className="block text-xs text-dark-400">
                    {t('companySettings.strictGeofencingDescription')}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('companySettings.saving')}
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                {t('companySettings.saveCompanySettings')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanySettings;